import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/Button';
import { ActionSheet } from '@/components/ui/ActionSheet';
import { createInviteToken } from '@/api/admin';
import { ApiError } from '@/api/client';
import { tripBasename } from '@/lib/env';
import { toast } from '@/store/toasts';
import styles from './QrInviteModal.module.css';

interface QrInviteModalProps {
  slug: string;
  open: boolean;
  onClose: () => void;
}

interface InviteState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  token?: string;
  expiresAt?: number;
  url?: string;
  qrDataUrl?: string;
  errorMsg?: string;
}

/**
 * Holt beim Öffnen den dauerhaften, mehrfach nutzbaren Trip-Invite-Token vom
 * Backend und zeigt den `/trips/<slug>/auth?invite=…`-Link als QR-Code und
 * kopierbaren String. Web-Share-API wird genutzt falls verfügbar
 * (Mobile-Sheets für Whatsapp/Signal/etc.).
 *
 * Backend-Endpoint prüft Trip-Session — der Aufrufer muss daher als Trip-User
 * eingeloggt sein. Bei 401/403 zeigen wir einen Hinweis mit Direktlink auf
 * /trips/<slug>/auth.
 */
export function QrInviteModal({ slug, open, onClose }: QrInviteModalProps) {
  const [state, setState] = useState<InviteState>({ status: 'idle' });

  useEffect(() => {
    if (!open) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      try {
        const res = await createInviteToken(slug);
        if (cancelled) return;
        const url = `${window.location.origin}${tripBasename(slug)}/auth?invite=${encodeURIComponent(res.token)}`;
        const qrDataUrl = await QRCode.toDataURL(url, {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 280,
          color: { dark: '#1a1f2e', light: '#ffffff' },
        });
        if (cancelled) return;
        setState({ status: 'ready', token: res.token, expiresAt: res.expiresAt, url, qrDataUrl });
      } catch (err) {
        if (cancelled) return;
        const status = err instanceof ApiError ? err.status : 0;
        const msg = status === 401 || status === 403
          ? 'Du brauchst eine Trip-Session, um Invites zu erzeugen. Logge dich kurz unter /trips/<slug>/auth ein.'
          : err instanceof Error ? err.message : 'Unbekannter Fehler';
        setState({ status: 'error', errorMsg: msg });
      }
    })();
    return () => { cancelled = true; };
  }, [open, slug]);

  async function onCopy() {
    if (!state.url) return;
    try {
      await navigator.clipboard.writeText(state.url);
      toast.success('Link in die Zwischenablage kopiert');
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  }

  async function onShare() {
    if (!state.url) return;
    if (typeof navigator.share !== 'function') {
      toast.info('Web-Share nicht verfügbar — Link wurde kopiert');
      await onCopy();
      return;
    }
    try {
      await navigator.share({
        title: 'Mallorca-Trip-Einladung',
        text: 'Du bist eingeladen! Klick den Link, um beizutreten:',
        url: state.url,
      });
    } catch {
      // User-Cancel ist OK, nichts toasten.
    }
  }

  return (
    <ActionSheet open={open} onClose={onClose} title="Trip-Einladung">
      {state.status === 'loading' ? (
        <div className={styles.placeholder}>Erzeuge Invite-Token …</div>
      ) : state.status === 'error' ? (
        <div className={styles.errorBox}>
          {state.errorMsg}
          {state.errorMsg?.includes('Trip-Session') ? (
            <a
              href={`/trips/${encodeURIComponent(slug)}/auth`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.errorLink}
            >
              → Zur Trip-Auth-Seite
            </a>
          ) : null}
        </div>
      ) : state.status === 'ready' && state.qrDataUrl && state.url ? (
        <>
          <div className={styles.qrWrap}>
            <img src={state.qrDataUrl} alt="Invite-QR-Code" className={styles.qr} />
          </div>
          <div className={styles.url} title={state.url}>{state.url}</div>
          {state.expiresAt ? (
            <div className={styles.expires}>
              Gültig bis {new Date(state.expiresAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
              {' '}· Einmal-Code
            </div>
          ) : (
            <div className={styles.expires}>Dauerhaft gültig · für die ganze Crew</div>
          )}
          <div className={styles.actions}>
            <Button variant="primary" onClick={onShare}>📤 Teilen</Button>
            <Button variant="secondary" onClick={onCopy}>📋 Link kopieren</Button>
          </div>
        </>
      ) : null}
      <Button variant="ghost" onClick={onClose}>Schließen</Button>
    </ActionSheet>
  );
}
